import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Group, MultiSelect, Text, useMantineColorScheme } from "@mantine/core";
import HighchartsReact from "highcharts-react-official";
import MetricSelection, { METRIC_GROUPS, ATTRIBUTES_LABELS } from "./MetricSelection";
import { getColorForKey, colorForGroupIndex } from "./chartConstants";
import { useTranslation } from "react-i18next";
import { formatCompactNumber, useCurrency } from "../../utils/helpers";

if (typeof window !== "undefined") {
  if (!window.CSS) window.CSS = {};
  if (typeof window.CSS.supports !== "function") {
    window.CSS.supports = () => false;
  }
}

const highstockModule = require("highcharts/highstock");
const Highcharts = highstockModule.default || highstockModule;

const exportingModule = require("highcharts/modules/exporting");
if (typeof exportingModule === "function") {
  exportingModule(Highcharts);
} else if (typeof exportingModule?.default === "function") {
  exportingModule.default(Highcharts);
}

const EXCLUDED_ATTRIBUTES = [
  "Thermal Supply CO2 (kg)",
  "Unmet Energy Off Grid (kWh)",
  "Unmet Energy OnGrid (kWh)",
  "Excess Energy OffGrid (kWh)",
  "Excess Energy Grid Connected (kWh)",
];

const normalizeValue = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    if (value === "Infinity" || value === "NaN") return 0;
    const parsed = Number(String(value).replace(/[^\d.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const csvEscape = (value) => {
  const str = String(value ?? "");
  return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const formatYAxisLabel = function formatLabel() {
  return formatCompactNumber(this.value);
};

const createTooltipFormatter = (chartCategories) => function formatTooltip() {
  const color = this?.point?.color || this?.color || "#999";
  const seriesName = this?.series?.name || "";
  const xIndex = Number.isFinite(this?.point?.x) ? this.point.x : null;
  const scenarioLabel =
    typeof this?.point?.category === "string"
      ? this.point.category
      : (xIndex !== null && chartCategories[xIndex]) || "";
  return `<div style=\"font-weight:700;margin-bottom:6px;\">${scenarioLabel}</div><div style=\"display:flex;align-items:center;gap:6px;\"><span style=\"display:inline-block;width:10px;height:10px;background:${color};border-radius:2px;\"></span><span>${seriesName}: <b>${formatCompactNumber(this?.y)}</b></span></div>`;
};

const CompareChart = ({ data }) => {
  const chartRef = useRef(null);
  const initializedScenariosRef = useRef(false);
  const { colorScheme } = useMantineColorScheme();
  const { t } = useTranslation();
  const currency = useCurrency();
  const axisTextColor = colorScheme === "dark" ? "#d5d7e0" : "#495057";

  const [selectedAttributes, setSelectedAttributes] = useState(() => {
    const rates = METRIC_GROUPS.find((g) => g.id === "rates");
    return rates ? [...rates.keys] : [];
  });
  const [selectedScenarios, setSelectedScenarios] = useState([]);

  const scenarioLabelMap = useMemo(
    () => ({
      referenceExistingMicrogrid: t("result.scenario.existing"),
      completeMicrogrid: t("result.scenario.complete"),
    }),
    [t]
  );

  const getScenarioLabel = (scenarioKey) => scenarioLabelMap[scenarioKey] || scenarioKey;

  const attributeLabelToKey = (attr) => {
    if (!attr) return null;
    if (ATTRIBUTES_LABELS[attr]) return attr;

    const exact = Object.keys(ATTRIBUTES_LABELS).find((key) => {
      const label = ATTRIBUTES_LABELS[key] || "";
      return label === attr || label.replace("{currency}", currency) === attr;
    });
    if (exact) return exact;

    const ciKey = Object.keys(ATTRIBUTES_LABELS).find(
      (key) => key.toLowerCase() === String(attr).toLowerCase()
    );
    if (ciKey) return ciKey;

    const normalize = (val) =>
      String(val || "")
        .toLowerCase()
        .replace(/\{currency\}/g, "")
        .replace(/\$|usd|eur|gbp|€|£/g, "")
        .replace(/[^a-z0-9]/g, "");

    const normalizedAttr = normalize(attr);
    return (
      Object.keys(ATTRIBUTES_LABELS).find((key) => {
        const label = ATTRIBUTES_LABELS[key] || "";
        return normalize(label) === normalizedAttr || normalize(label.replace("{currency}", currency)) === normalizedAttr;
      }) || null
    );
  };

  const allScenarioKeys = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];
    return Object.keys(data[0]).filter((key) => key !== "attribute");
  }, [data]);

  useEffect(() => {
    if (allScenarioKeys.length === 0) return;
    if (initializedScenariosRef.current) return;

    if (!selectedScenarios || selectedScenarios.length === 0) {
      const defaults = [];
      if (allScenarioKeys.includes("referenceExistingMicrogrid")) {
        defaults.push("referenceExistingMicrogrid");
      }
      if (allScenarioKeys.includes("completeMicrogrid")) {
        defaults.push("completeMicrogrid");
      } else {
        const fallback = allScenarioKeys.find((key) => key !== "referenceExistingMicrogrid");
        if (fallback) defaults.push(fallback);
      }
      if (defaults.length === 0) defaults.push(allScenarioKeys[0]);
      setSelectedScenarios(Array.from(new Set(defaults)));
    }

    initializedScenariosRef.current = true;
  }, [allScenarioKeys, selectedScenarios]);

  useEffect(() => {
    if ((!selectedAttributes || selectedAttributes.length === 0) && Array.isArray(data) && data.length > 0) {
      const allRows = data.filter((row) => !EXCLUDED_ATTRIBUTES.includes(row.attribute));
      setSelectedAttributes(allRows.map((row) => attributeLabelToKey(row.attribute) || row.attribute));
    }
  }, [data]);

  const allRows = useMemo(
    () => (Array.isArray(data) ? data.filter((row) => !EXCLUDED_ATTRIBUTES.includes(row.attribute)) : []),
    [data]
  );

  const visibleRows = useMemo(() => {
    const selectedKeys = new Set((selectedAttributes || []).map((item) => attributeLabelToKey(item) || item));
    return allRows.filter((row) => {
      const key = attributeLabelToKey(row.attribute) || row.attribute;
      return selectedKeys.has(key) || selectedKeys.has(row.attribute);
    });
  }, [allRows, selectedAttributes]);

  const chartCategories = useMemo(
    () => (selectedScenarios || []).map((scenarioKey) => getScenarioLabel(scenarioKey)),
    [selectedScenarios, scenarioLabelMap]
  );

  const chartSeries = useMemo(
    () =>
      visibleRows.map((row) => {
        const key = attributeLabelToKey(row.attribute) || row.attribute;
        const group = METRIC_GROUPS.find((metricGroup) => metricGroup.keys.includes(key));
        const indexInGroup = group ? group.keys.indexOf(key) : -1;
        const color = indexInGroup >= 0 ? colorForGroupIndex(indexInGroup) : getColorForKey(key);

        return {
          id: key,
          name: row.attribute,
          type: "column",
          color,
          borderWidth: 0,
          data: (selectedScenarios || []).map((scenarioKey) => normalizeValue(row[scenarioKey])),
        };
      }),
    [visibleRows, selectedScenarios]
  );

  const yAxisUnit = useMemo(() => {
    const firstKey = Array.isArray(selectedAttributes) && selectedAttributes.length > 0 ? selectedAttributes[0] : null;
    const selectedGroup = METRIC_GROUPS.find((group) => group.keys.includes(firstKey));
    if (!selectedGroup) return currency;

    if (selectedGroup.id === "rates") return "%";
    if (selectedGroup.id === "unit_costs") return `${currency}/kWh`;
    if (selectedGroup.id === "time") return "Years";
    if (selectedGroup.id === "energy") return "kWh";
    if (selectedGroup.id === "emissions") return "kg CO2";
    return currency;
  }, [selectedAttributes, currency]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "column",
        height: 540,
        backgroundColor: colorScheme === "dark" ? "#111111" : "#ffffff",
        style: { fontFamily: "Nunito, sans-serif" },
        spacingTop: 18,
        spacingRight: 10,
      },
      accessibility: { enabled: false },
      title: { text: null },
      credits: { enabled: false },
      legend: { enabled: false },
      exporting: { enabled: false },
      xAxis: {
        categories: chartCategories,
        gridLineWidth: 1,
        gridLineColor: colorScheme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)",
        title: {
          text: t("result.compare.axis.scenario"),
          style: { color: axisTextColor, fontSize: "13px", fontWeight: 600 },
        },
        labels: {
          style: { color: axisTextColor, fontSize: "11px" },
          rotation: chartCategories.length > 6 ? -30 : 0,
        },
      },
      yAxis: {
        title: {
          text: `${t("result.compare.axis.value")} (${yAxisUnit})`,
          style: { color: axisTextColor, fontSize: "13px", fontWeight: 600 },
        },
        labels: {
          style: { color: axisTextColor, fontSize: "11px" },
          formatter: formatYAxisLabel,
        },
        gridLineWidth: 1,
        gridLineColor: colorScheme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)",
        plotLines: [{ value: 0, color: "#888", width: 1, zIndex: 2 }],
      },
      tooltip: {
        shared: false,
        useHTML: true,
        backgroundColor: colorScheme === "dark" ? "#222832" : "#fff",
        borderColor: colorScheme === "dark" ? "#555" : "#888",
        style: { color: colorScheme === "dark" ? "#d5d7e0" : "#222" },
        formatter: createTooltipFormatter(chartCategories),
      },
      plotOptions: {
        column: {
          borderRadius: 2,
          pointPadding: 0.06,
          groupPadding: 0.12,
          minPointLength: 2,
          states: {
            hover: { brightness: -0.05 },
          },
        },
        series: {
          animation: false,
        },
      },
      series: chartSeries,
      noData: {
        style: { color: axisTextColor, fontSize: "13px" },
      },
    }),
    [chartCategories, chartSeries, colorScheme, axisTextColor, t, yAxisUnit]
  );

  const handleDownloadPng = () => {
    const chart = chartRef.current?.chart;
    if (!chart) return;
    if (typeof chart.exportChartLocal === "function") {
      chart.exportChartLocal({ type: "image/png", filename: "compare-chart" });
      return;
    }
    if (typeof chart.exportChart === "function") {
      chart.exportChart({ type: "image/png", filename: "compare-chart" });
    }
  };

  const handleDownloadCsv = () => {
    if (!selectedScenarios?.length || !visibleRows?.length) return;
    const header = ["Attribute", ...selectedScenarios.map((key) => getScenarioLabel(key))];
    const rows = visibleRows.map((row) => [
      row.attribute,
      ...selectedScenarios.map((scenarioKey) => normalizeValue(row[scenarioKey])),
    ]);
    const csv = [header, ...rows].map((row) => row.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "compare-chart.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const handleZoom = (factor) => {
    const chart = chartRef.current?.chart;
    const axis = chart?.yAxis?.[0];
    if (!axis) return;

    const dataMin = Number.isFinite(axis.dataMin) ? axis.dataMin : 0;
    const dataMax = Number.isFinite(axis.dataMax) ? axis.dataMax : 1;
    const currentMin = Number.isFinite(axis.min) ? axis.min : dataMin;
    const currentMax = Number.isFinite(axis.max) ? axis.max : dataMax;
    const currentSpan = currentMax - currentMin;
    const safeSpan = currentSpan > 0 ? currentSpan : Math.max(1, dataMax - dataMin);

    const nextSpan = safeSpan / factor;
    const center = currentMin + safeSpan / 2;
    let nextMin = center - nextSpan / 2;
    let nextMax = center + nextSpan / 2;

    if (nextMin < dataMin) {
      nextMax += dataMin - nextMin;
      nextMin = dataMin;
    }
    if (nextMax > dataMax) {
      nextMin -= nextMax - dataMax;
      nextMax = dataMax;
    }

    axis.setExtremes(nextMin, nextMax, true, false);
  };

  const resetZoom = () => {
    const chart = chartRef.current?.chart;
    const axis = chart?.yAxis?.[0];
    if (!axis) return;
    axis.setExtremes(null, null, true, false);
  };

  const hasScenarios = Array.isArray(selectedScenarios) && selectedScenarios.length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-start", width: "100%", minHeight: 540, position: "relative", zoom: "118%" }}>
      <div style={{ width: "80%", minHeight: 540, position: "relative" }}>
        <div style={{ width: "100%", marginBottom: 8, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            {selectedAttributes && selectedAttributes.length > 0 ? (
              selectedAttributes.map((key) => {
                const groupObj = METRIC_GROUPS.find((g) => g.keys.includes(key));
                const idx = groupObj ? groupObj.keys.indexOf(key) : -1;
                const color = idx >= 0 ? colorForGroupIndex(idx) : getColorForKey(key);
                const label = (ATTRIBUTES_LABELS[key] || key).replace("{currency}", currency);
                return (
                  <div key={key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 6px", borderRadius: 4, background: colorScheme === "dark" ? "#1a1d21" : "#f8f9fa" }}>
                    <div style={{ width: 12, height: 12, background: color, borderRadius: 2 }} />
                    <div style={{ fontWeight: 700, fontSize: 12, color: axisTextColor }}>{label}</div>
                    <button
                      type="button"
                      onClick={() => setSelectedAttributes(selectedAttributes.filter((selected) => selected !== key))}
                      style={{ marginLeft: 4, border: "none", background: "transparent", cursor: "pointer", color: axisTextColor, fontSize: 12, fontWeight: 700 }}
                      title={t("result.compare.removeMetric") || "Remove metric"}
                    >
                      x
                    </button>
                  </div>
                );
              })
            ) : (
              <Text size="sm" c="dimmed">{t("result.compare.noSelection")}</Text>
            )}
          </div>

          <Group spacing={6} style={{ alignSelf: "flex-end", justifyContent: "flex-end" }}>
            <Button size="xs" variant="default" onClick={() => handleZoom(1.5)} title={t("timeSeries.toolbar.zoomIn") || "Zoom in"}>+
            </Button>
            <Button size="xs" variant="default" onClick={() => handleZoom(1 / 1.5)} title={t("timeSeries.toolbar.zoomOut") || "Zoom out"}>-
            </Button>
            <Button size="xs" variant="default" onClick={resetZoom}>{t("timeSeries.toolbar.reset") || "Reset"}</Button>
            <select
              aria-label="compare-chart-download"
              defaultValue=""
              onChange={(event) => {
                const action = event.target.value;
                if (action === "png") handleDownloadPng();
                if (action === "csv") handleDownloadCsv();
                event.target.value = "";
              }}
              style={{ padding: "5px 8px", borderRadius: 4, border: "1px solid #ced4da", background: colorScheme === "dark" ? "#1f2328" : "#fff", color: axisTextColor }}
            >
              <option value="" disabled hidden>
                {t("timeSeries.toolbar.download") || "Download"}
              </option>
              <option value="png">PNG</option>
              <option value="csv">CSV</option>
            </select>
          </Group>
        </div>

        {hasScenarios ? (
          <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
        ) : (
          <div style={{ minHeight: 540, display: "flex", alignItems: "center", justifyContent: "center", color: axisTextColor }}>
            {t("result.compare.noScenarios") || "No scenarios selected"}
          </div>
        )}
      </div>

      <div style={{ width: "20%", marginLeft: 24, marginTop: 10, display: "flex", flexDirection: "column", minHeight: 540 }}>
        <div style={{ marginBottom: 12 }}>
          <MetricSelection
            label={t("result.compare.select.metrics")}
            value={selectedAttributes}
            onChange={setSelectedAttributes}
            currency={currency}
            showSelectedInTrigger={false}
          />
        </div>

        <div style={{ marginTop: 12 }}>
          <MultiSelect
            data={allScenarioKeys.map((key) => ({ value: key, label: getScenarioLabel(key) }))}
            value={selectedScenarios}
            onChange={setSelectedScenarios}
            label={t("result.compare.select.label")}
            placeholder={t("result.compare.select.placeholder")}
            nothingFound={t("result.compare.select.nothingFound")}
            searchable
            clearable
          />
        </div>
      </div>
    </div>
  );
};

export default CompareChart;
